import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Details } from './details-schema';

type TermsContent = { checked: boolean; text: string; label: string } | undefined;

export const usePersistedStore = create<{
	_hasHydrated: boolean;
	setHasHydrated: (state: boolean) => void;
	amount: number | undefined;
	setAmount: (amount: number | undefined) => void;
	details: Details;
	setDetails: (data: Details | ((prev: Details) => Details)) => void;
	termsContent: TermsContent;
	setTermsContent: (data: TermsContent) => void;
	ngosTermsContent: TermsContent;
	setNgosTermsContent: (data: TermsContent) => void;
	telefonicaTermsContent: TermsContent;
	setTelefonicaTermsContent: (data: TermsContent) => void;
}>()(
	persist(
		(set) => ({
			_hasHydrated: false,
			setHasHydrated: (state) => {
				set({
					_hasHydrated: state,
				});
			},
			amount: undefined,
			setAmount: (amount) => set(() => ({ amount })),
			details: {
				firstname: '',
				lastname: '',
				email: '',
				repeatEmail: '',
				certificate: false,
				documentType: 'id',
				documentNumber: '',
				streetName: '',
				streetNumber: '',
				additionalInfo: '',
				city: '',
				cp: '',
				country: 'ES',
				province: '',
				region: '',
				termsAndConditions: false,
				ngosTermsAndConditions: false,
				telefonicaTermsAndConditions: false,
				newsletter: false,
			},
			setDetails: (details) =>
				set((state) => ({ details: typeof details === 'function' ? details(state.details) : details })),
			termsContent: undefined,
			setTermsContent: (termsContent) => set(() => ({ termsContent })),
			ngosTermsContent: undefined,
			setNgosTermsContent: (ngosTermsContent) => set(() => ({ ngosTermsContent })),
			telefonicaTermsContent: undefined,
			setTelefonicaTermsContent: (telefonicaTermsContent) => set(() => ({ telefonicaTermsContent })),
		}),
		{
			// Bump this version when detailsSchema shape changes in a non-backward-compatible way to invalidate persisted storage.
			name: `app-storage-v1`,
			onRehydrateStorage: (state) => {
				return () => state.setHasHydrated(true);
			},
		},
	),
);

const CACHE_TTL_MS = 10 * 60 * 1000;

export const useEphemeralStore = create<{
	pagesLoaded: string[];
	setPagesLoaded: (pagesLoaded: string[]) => void;
	donationToConfirm: { id: string; campaignId: string } | undefined;
	setDonationToConfirm: (donationToConfirm: { id: string; campaignId: string } | undefined) => void;
	donationConfirmed: boolean;
	setDonationConfirmed: (donationConfirmed: boolean) => void;
	cachedPreregistration: { donationId: string; paymentToken: string; captchaPassed: boolean } | undefined;
	setCachedPreregistration: (
		data: { donationId: string; paymentToken: string; captchaPassed: boolean } | undefined,
	) => void;
	cachedPaymentMethods: unknown;
	cachedPaymentMethodsAt: number | undefined;
	setCachedPaymentMethods: (data: unknown) => void;
	getCachedPaymentMethods: () => unknown;
	clearCheckoutCache: () => void;
}>((set, get) => ({
	pagesLoaded: [],
	setPagesLoaded: (pagesLoaded: string[]) => set({ pagesLoaded }),
	donationToConfirm: undefined,
	setDonationToConfirm: (donationToConfirm) => set(() => ({ donationToConfirm })),
	donationConfirmed: false,
	setDonationConfirmed: (donationConfirmed) => set(() => ({ donationConfirmed })),
	cachedPreregistration: undefined,
	setCachedPreregistration: (cachedPreregistration) => set(() => ({ cachedPreregistration })),
	cachedPaymentMethods: undefined,
	cachedPaymentMethodsAt: undefined,
	setCachedPaymentMethods: (cachedPaymentMethods) =>
		set(() => ({ cachedPaymentMethods, cachedPaymentMethodsAt: Date.now() })),
	getCachedPaymentMethods: () => {
		const { cachedPaymentMethods, cachedPaymentMethodsAt } = get();
		if (!cachedPaymentMethods || !cachedPaymentMethodsAt) return undefined;
		if (Date.now() - cachedPaymentMethodsAt > CACHE_TTL_MS) return undefined;
		return cachedPaymentMethods;
	},
	clearCheckoutCache: () =>
		set(() => ({
			cachedPreregistration: undefined,
			cachedPaymentMethods: undefined,
			cachedPaymentMethodsAt: undefined,
		})),
}));
